import React, { useState } from 'react';
import { ApiReferenceReact } from '@scalar/api-reference-react';
import '@scalar/api-reference-react/style.css';
import { useQuery } from 'react-query';
import { Alert, Box, Chip, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { getScopedToken } from '../../api/developerApi';
import { API_CONFIG } from '../../config';

// Per-tenant playground config. The toggle switches which tenant_id the minted
// st_* token carries — NOT the host (the deploy pipeline injects one partner-API
// origin as the spec's `servers:` block, #454) — so "Try it" runs against the
// developer's live vs test tenant DATA at the same host. Live tokens already
// reach the browser for the Keys/Logs pages, so this adds no new boundary; we
// mint live at a 15-min TTL (vs test's 1hr) to narrow live blast radius, and
// refresh before expiry so the injected credential stays valid.
const ENV_CONFIG = {
  test: { ttl: undefined, refreshMs: 50 * 60 * 1000 }, // server default 1hr token
  live: { ttl: 900, refreshMs: 13 * 60 * 1000 }, // 15-min token
};

// Host the "Try it" requests target. Surfacing it matches where requests go
// (the deploy-injected `servers:` origin). Falls back to the raw base if it
// isn't a parseable absolute URL (e.g. a relative dev override). `base`
// defaults to the configured partner base; the param keeps it purely testable.
export function partnerApiHost(base = API_CONFIG.partnerApiBase || '') {
  try {
    return new URL(base).host;
  } catch {
    return base || '';
  }
}

// Map the token query state to a user-facing chip. Surfacing the state (instead
// of silently falling back to an empty token) is the #455 ask: a developer who
// sees "unavailable" knows to paste their own key rather than wondering why
// "Try it" 401s.
function tokenChip({ isLoading, isError, data }) {
  if (isLoading) return { severity: 'info', label: 'Minting token…', color: 'default' };
  if (isError || !data?.token) return { severity: 'warning', label: 'Token unavailable', color: 'error' };
  const expiresAt = data.expiresAt ? new Date(data.expiresAt * 1000) : null;
  if (expiresAt && expiresAt.getTime() <= Date.now()) {
    return { severity: 'warning', label: 'Token expired', color: 'warning' };
  }
  const expiryText = expiresAt
    ? ` (expires ${expiresAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})`
    : '';
  return { severity: 'success', label: `Token active${expiryText}`, color: 'success' };
}

export default function DocsPage() {
  // Default to the test tenant — the safe default, and preserves the prior
  // (test-only) behavior for anyone who never touches the toggle.
  const [env, setEnv] = useState('test');
  const cfg = ENV_CONFIG[env];

  // Re-keyed on env so switching re-mints. refetchInterval refreshes the token
  // ahead of its TTL while the tab is focused; react-query pauses interval
  // refetch on a backgrounded tab, so a long-idle tab can still hit an expired
  // token until refocus — the "Token expired" chip surfaces that case.
  const query = useQuery(['scoped-token', env], () => getScopedToken(env, cfg.ttl), {
    staleTime: cfg.refreshMs,
    refetchInterval: cfg.refreshMs,
    retry: 1,
  });

  const chip = tokenChip(query);
  const host = partnerApiHost();

  const handleEnv = (_e, next) => {
    if (next) setEnv(next); // ignore the deselect-to-null click
  };

  return (
    // Negative margin escapes the portal's p:{xs:2, sm:3} content padding
    // so Scalar can render its own full-width layout flush to the content area edges.
    <Box sx={{ mx: { xs: -2, sm: -3 }, mt: { xs: -2, sm: -3 } }}>
      <Alert severity={chip.severity} icon={false} sx={{ borderRadius: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
          <Typography variant="subtitle2" component="span" sx={{ fontWeight: 700 }}>
            API playground
          </Typography>
          <ToggleButtonGroup size="small" exclusive value={env} onChange={handleEnv} aria-label="Playground tenant">
            <ToggleButton value="test" aria-label="Test tenant" sx={{ py: 0, px: 1.25, textTransform: 'none' }}>
              Test
            </ToggleButton>
            <ToggleButton value="live" aria-label="Live tenant" sx={{ py: 0, px: 1.25, textTransform: 'none' }}>
              Live
            </ToggleButton>
          </ToggleButtonGroup>
          <Chip size="small" label={chip.label} color={chip.color} />
        </Box>
        <Typography variant="body2">
          Authenticated as your <strong>{env}</strong> tenant via a temporary scoped token, injected
          automatically as the <strong>Bearer</strong> credential. <strong>Try it</strong> requests run
          against {host ? <code>{host}</code> : 'the partner API'}. Switch the environment above to target
          your other tenant, or paste your own API key into the <strong>Authorize</strong> field.
          {env === 'live' && (
            <>
              {' '}
              <strong>Live requests act on real data.</strong>
            </>
          )}
        </Typography>
      </Alert>
      <ApiReferenceReact
        // Force a fresh mount per env: Scalar 0.9.40 reads `authentication` at
        // init and does not reliably re-apply a changed token prop after mount,
        // so a plain prop update would leave the prior tenant's token pre-filled.
        key={env}
        configuration={{
          spec: { url: '/openapi.yaml' },
          // Scalar 0.9.40 (@scalar/types 0.12.1) reads pre-filled credentials from
          // `authentication.securitySchemes.<schemeName>.token` — NOT the legacy
          // flat `authentication.bearer.token`, which this used to pass and which
          // Scalar silently ignored, so the token never pre-filled. 'BearerAuth' is
          // the scheme name the public partner spec defines (OpenAPIFileTransformer,
          // `type: http, scheme: bearer`).
          authentication: {
            preferredSecurityScheme: 'BearerAuth',
            securitySchemes: {
              BearerAuth: { token: query.data?.token ?? '' },
            },
          },
          hideDownloadButton: false,
          theme: 'default',
          // Scalar loads its own fonts from fonts.scalar.com unless told not to. This site's
          // Content-Security-Policy allows fonts only from itself (font-src 'self' data:), so every one of
          // those requests was blocked and logged as a console error. Turning the option off
          // keeps the policy tight (no third-party host added) and the page falls back to the
          // system fonts.
          withDefaultFonts: false,
        }}
      />
    </Box>
  );
}
